import { useState } from "react";

import { CheckIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

import { IFitmentAlternative, IFitmentCheck, IInboxConversation } from "../../models/messagesInbox/IInboxConversation";
import classNames from "../../utils/classNames";
import { SAMPLE_REG_REQUEST } from "../../stubs/messagesInboxSamples";
import { displayReg, normaliseReg } from "../../utils/detectRegs";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_TIME } from "../../utils/inboxDates";
import { partKeyCode } from "../../utils/partKeyCode";
import PriceFormat from "../../utils/priceFormat";
import { PlateChip, PreviewNote, ResultIcon, useInboxLive } from "./InboxBits";

const overallStyle = {
  fits: { label: "Fits", icon: CheckIcon, box: "border-green-200", head: "bg-green-50 text-green-800" },
  check: { label: "Check details", icon: ExclamationTriangleIcon, box: "border-amber-200", head: "bg-amber-50 text-amber-800" },
  no_fit: { label: "Doesn't fit", icon: XMarkIcon, box: "border-red-200", head: "bg-red-50 text-red-800" },
};

function clock(iso: string) {
  return formatDateTime(iso, INBOX_TIME);
}

/** A reply drafted from the compare result. It goes into the composer; nothing is sent. */
export function fitmentAnswer(c: IInboxConversation, fitment: IFitmentCheck, alternatives?: IFitmentAlternative[]) {
  const reg = displayReg(fitment.reg);
  const donor = c.donor;
  const donorText = donor ? `${[donor.year, donor.make, donor.model, donor.variant].filter(Boolean).join(" ")}${donor.engineCode ? `, engine code ${donor.engineCode}` : ""}` : "the donor car";
  const partName = c.part?.shortTitle.toLowerCase() ?? "part";
  const oe = c.part?.oePartNumber ? ` and part number ${c.part.oePartNumber}` : "";
  const key = partKeyCode(c.part?.categoryName, c.donor);
  const keyText = key?.value ? ` – it's a ${key.value}, the same ${key.field.toLowerCase()} as yours` : "";

  if (fitment.overall === "fits" && key) {
    return `Hi {buyer_first_name}, thanks for your message. We've checked ${reg} against our donor car (${donorText}) and this ${partName} is a match for your car${keyText}.\n\nKind regards,\n{shop_name}`;
  }
  if (fitment.overall === "fits") {
    return `Hi {buyer_first_name}, thanks for your message. We've checked ${reg} against our donor car (${donorText}) and this ${partName} is a match for your car – same series, body${oe}.\n\nKind regards,\n{shop_name}`;
  }
  if (fitment.overall === "check") {
    const differences = fitment.rows.filter((r) => r.result !== "match").map((r) => r.field.toLowerCase());
    return `Hi {buyer_first_name}, thanks for your message. We've checked ${reg} against our donor car (${donorText}). The model, series and body match; the ${differences.join(", ")} differ, which shouldn't matter for this part, but ${fitment.note ? fitment.note.charAt(0).toLowerCase() + fitment.note.slice(1) : "please check before ordering."}\n\nKind regards,\n{shop_name}`;
  }
  const alternativeText = alternatives && alternatives.length > 0 ? ` We do have one that should fit your car: ${alternatives[0].listingUrl ?? alternatives[0].title}` : "";
  return `Hi {buyer_first_name}, thanks for your message. We've checked ${reg} against our donor car (${donorText}) and unfortunately this ${partName} isn't a match for your car.${fitment.note ? ` ${fitment.note}` : ""}${alternativeText}\n\nKind regards,\n{shop_name}`;
}

interface FitmentCardProps {
  conversation: IInboxConversation;
  /** Reg found in the thread or typed in; null when the buyer hasn't given one. */
  reg: string | null;
  onReg: (reg: string) => void;
  onCompare: (reg: string) => void;
  onSendAnswer: (text: string) => void;
  onAddToReply: (text: string) => void;
}

export default function FitmentCard({ conversation: c, reg, onReg, onCompare, onSendAnswer, onAddToReply }: FitmentCardProps) {
  const { t } = useTranslation("messages");
  // The paid donor compare isn't wired to a real lookup yet, so on live data it stops at the reg.
  const live = useInboxLive();
  const [manualReg, setManualReg] = useState("");
  const [editing, setEditing] = useState(false);
  const fitment = c.fitment && (!reg || normaliseReg(reg) === c.fitment.reg) ? c.fitment : undefined;
  const key = partKeyCode(c.part?.categoryName, c.donor);
  const keyNote = key && (
    <p className="mt-2 rounded-md bg-gray-50 px-2.5 py-1.5 text-[12px] text-gray-600">
      {key.field === "Engine code" ? t("Engines are matched on engine code, not part number.") : t("Gearboxes are matched on gearbox code, not part number.")}
      {key.value ? (
        <>
          {" "}{t("Donor:")} <span className="font-mono font-semibold text-gray-900">{key.value}</span>
        </>
      ) : (
        <span className="text-amber-700"> {key.field === "Engine code" ? t("The donor has no engine code recorded.") : t("The donor has no gearbox code recorded.")}</span>
      )}
    </p>
  );

  const manualEntry = (
    <form
      className="mt-3 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (manualReg.trim()) {
          onReg(normaliseReg(manualReg));
          setManualReg("");
          setEditing(false);
        }
      }}
    >
      <input
        value={manualReg}
        onChange={(e) => setManualReg(e.target.value)}
        placeholder={t("Type a reg or VIN")}
        className="min-w-0 flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 font-mono text-[13px] uppercase focus:border-primary-500 focus:ring-primary-500"
      />
      <button type="submit" className="rounded-lg bg-primary-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-primary-700">
        {live ? t("Use this reg") : t("Check fitment")}
      </button>
    </form>
  );

  // (a) No reg or VIN in the conversation yet.
  if (!reg && !fitment) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t("Fitment check")}</p>
        <p className="mt-1 text-[15px] font-semibold text-gray-900">{t("Waiting for buyer's reg")}</p>
        <p className="mt-1 text-[13px] text-gray-600">
          {t("No reg or VIN found in the thread.")}
          {c.regRequestSentAt && <> {t("An automatic reply asking for their registration was sent at {{time}}.", { time: clock(c.regRequestSentAt) })}</>}
        </p>
        {c.buyerVehicleDescription && (
          <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-[12px] text-amber-900">
            {t("They described their car as")} <span className="font-semibold">{c.buyerVehicleDescription}</span> {t("– that isn't enough to check, we need the reg or VIN.")}
          </p>
        )}
        {!c.regRequestSentAt && (
          // TODO(stuart): the wording comes from InboxSettings once it's stored (plan §7).
          <button type="button" onClick={() => onSendAnswer(SAMPLE_REG_REQUEST)} className="mt-3 w-full rounded-lg bg-primary-600 py-2 text-[14px] font-medium text-white hover:bg-primary-700">
            {t("Draft reg / VIN request")}
          </button>
        )}
        {keyNote}
        {manualEntry}
        {!live && <PreviewNote>{t("Each real lookup is paid for, so it only runs when you click – and each reg is only looked up once.")}</PreviewNote>}
      </section>
    );
  }

  // (b) Reg found but not compared yet.
  if (!fitment && reg) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t("Fitment check")}</p>
        <p className="mt-1 text-[15px] font-semibold text-gray-900">{t("Reg found in message")}</p>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[13px]">
          <div>
            <p className="text-[11px] uppercase text-gray-500">{t("Buyer's car")}</p>
            <div className="mt-1">
              <PlateChip reg={reg} />
            </div>
          </div>
          {c.donor && (
            <div>
              <p className="text-[11px] uppercase text-gray-500">{t("Donor")}</p>
              <div className="mt-1">
                <PlateChip reg={c.donor.vrm} />
              </div>
              <p className="mt-1 text-gray-600">
                {c.donor.year} {c.donor.make} {c.donor.model}
              </p>
            </div>
          )}
        </div>
        {keyNote}
        {live ? (
          <p className="mt-3 rounded-md bg-gray-50 px-2.5 py-1.5 text-[12px] text-gray-600">{t("The donor compare isn't switched on yet – check the reg against the donor yourself, or use Parts that fit below.")}</p>
        ) : (
          <button type="button" onClick={() => onCompare(reg)} className="mt-4 w-full rounded-lg bg-primary-600 py-2 text-[14px] font-medium text-white hover:bg-primary-700">
            {t("Compare with donor")}
          </button>
        )}
        <div className="mt-2 flex justify-between text-[12px] text-gray-500">
          <span>{live ? "" : t("Uses one paid lookup, then remembered for this reg")}</span>
          <button type="button" onClick={() => setEditing(!editing)} className="text-primary-700 hover:underline">
            {t("Wrong reg? Edit")}
          </button>
        </div>
        {editing && manualEntry}
      </section>
    );
  }

  if (!fitment) return null;
  const style = overallStyle[fitment.overall];
  const Icon = style.icon;

  return (
    <div className="space-y-3">
      <section className={classNames("overflow-hidden rounded-xl border bg-white", style.box)}>
        <div className={classNames("flex items-start justify-between gap-3 px-4 py-3", style.head)}>
          <div className="shrink-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{t("Fitment check")}</p>
            <p className="mt-0.5 flex items-center gap-1.5 whitespace-nowrap text-[18px] font-semibold">
              <Icon className="h-5 w-5" /> {t(style.label)}
            </p>
          </div>
          <p className="min-w-0 text-right text-[12px] opacity-80">
            {t("Compared {{time}}", { time: clock(fitment.checkedAt) })}
            {fitment.buyerVehicleSummary && <span className="block">{fitment.buyerVehicleSummary}</span>}
          </p>
        </div>

        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-gray-100 text-left text-[11px] uppercase text-gray-500">
              <th className="px-4 py-2 font-medium" />
              <th className="py-2 font-medium">
                {t("Buyer's car")}
                <div className="mt-1">
                  <PlateChip reg={fitment.reg} />
                </div>
              </th>
              <th className="py-2 font-medium">
                {t("Donor")}
                {c.donor && (
                  <div className="mt-1">
                    <PlateChip reg={c.donor.vrm} />
                  </div>
                )}
              </th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {fitment.rows.map((row) => (
              <tr key={row.field} className={classNames("border-b border-gray-100 last:border-0", row.field === key?.field && "bg-gray-50 font-semibold")}>
                <td className="px-4 py-1.5 text-gray-500">
                  {t(row.field)}
                  {row.field === key?.field && <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-900">{t("Decides fit")}</span>}
                </td>
                <td className={classNames("py-1.5", row.result === "mismatch" ? "font-medium text-red-700" : "text-gray-900")}>{row.buyer}</td>
                <td className="py-1.5 text-gray-900">{row.donor}</td>
                <td className="pr-3">
                  <ResultIcon result={row.result} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {fitment.note && <p className="border-t border-gray-100 px-4 py-2 text-[12px] text-gray-600">{fitment.note}</p>}

        <div className="flex gap-2 px-4 pb-4 pt-2">
          <button type="button" onClick={() => onSendAnswer(fitmentAnswer(c, fitment, c.fitmentAlternatives))} className="flex-1 rounded-lg bg-primary-600 py-2 text-[14px] font-medium text-white hover:bg-primary-700">
            {t("Draft fitment reply")}
          </button>
          {!live && (
            <button type="button" onClick={() => onCompare(fitment.reg)} className="rounded-lg border border-gray-300 px-3 text-[13px] text-gray-700 hover:bg-gray-50">
              {t("Re-run")}
            </button>
          )}
        </div>
        <p className="-mt-2 px-4 pb-3 text-[11px] text-gray-400">{t("Puts a drafted reply in the box for you to check and send yourself.")}</p>
      </section>

      {fitment.overall === "no_fit" && (
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-[14px] font-semibold text-gray-900">{t("Parts we have that fit {{reg}}", { reg: displayReg(fitment.reg) })}</p>
          <p className="text-[12px] text-gray-500">
            {c.part?.categoryName || t("Same category")} · {t("whole stock")} · {t("{{count}} found", { count: c.fitmentAlternatives?.length ?? 0 })}
          </p>
          <ul className="mt-3 space-y-3">
            {(c.fitmentAlternatives ?? []).map((alt) => (
              <li key={alt.tagNumber} className="flex gap-3">
                <div className="h-12 w-12 shrink-0 rounded-md bg-gray-100" />
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-[13px] text-gray-900">{alt.title}</p>
                  <p className="text-[12px] text-gray-500">
                    {PriceFormat(alt.price)} · {t("Tag {{tag}}", { tag: alt.tagNumber })} · {alt.listed ?? <span className="text-amber-700">{t("Not listed")}</span>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onAddToReply(alt.listingUrl ? `\n\nThis one should fit your car: ${alt.listingUrl}` : `\n\nWe also have ${alt.title} (tag ${alt.tagNumber}) – we can list it for you.`)}
                  className="h-fit shrink-0 rounded-md border border-gray-300 px-2 py-1 text-[12px] text-gray-700 hover:bg-gray-50"
                >
                  {alt.listed ? t("Add link to reply") : t("Mention in reply")}
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-3 border-t border-gray-100 pt-3 text-[13px] text-gray-600">
            {t("None suitable?")} <span className="font-medium text-primary-700">{t("Create part request")}</span>
          </p>
          <PreviewNote>{t("Sample results. The real inbox searches your stock for the buyer's car without saving a new vehicle.")}</PreviewNote>
        </section>
      )}
    </div>
  );
}
